import { DateTime } from "luxon";

import { callSvcAxiosInstance } from "@/constants";
import { chatbotEndpoints } from "@/constants/endpoints";
import { ChatModel, ChatRequest, ChatbotHealthResponse, StreamEventData } from "@/types/chatbot.types";
import { normalizeError } from "@/utils/error.utils";

import { makeAuthenticatedStreamRequest } from "./httpUtils";

/**
 * Process server-sent event stream
 */
async function processStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    onChunk: (chunk: StreamEventData) => void,
    onComplete: () => void
): Promise<void> {
    const decoder = new TextDecoder();

    try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                onComplete();
                break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split("\n").filter((line) => line.trim());

            for (const line of lines) {
                if (line.startsWith("data: ")) {
                    try {
                        const data = line.slice(6); // Remove 'data: ' prefix
                        if (data === "[DONE]") {
                            onComplete();
                            return;
                        }

                        const streamEvent: StreamEventData = JSON.parse(data);
                        onChunk(streamEvent);

                        // Check if stream is complete
                        if (streamEvent.type === "done") {
                            onComplete();
                            break;
                        }
                    } catch (parseError) {
                        console.warn("Failed to parse stream chunk:", parseError);
                    }
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}

/**
 * Chatbot Service - Backend integration for AI chat functionality
 *
 * This service implements streaming chat API using server-sent events for real-time responses.
 */
class ChatbotService {
    /**
     * Check chatbot service health
     */
    async checkHealth(): Promise<ChatbotHealthResponse> {
        try {
            const response = await callSvcAxiosInstance.get(chatbotEndpoints.health);
            return response.data;
        } catch (error) {
            console.error("Failed to check chatbot health:", error);
            return {
                status: "unhealthy",
                message: "Service unavailable",
                timestamp: DateTime.now().toISO(),
            };
        }
    }

    /**
     * STREAMING CHAT API - Server-sent events approach
     * Send message and receive response chunks in real-time
     *
     
     * @param request Chat request data
     * @param callbacks Stream event callbacks
     * @returns AbortController to cancel the stream
     */
    async sendMessageStream(
        request: {
            message: string;
            userId?: string;
            chatId?: string;
            auth?: unknown;
        },
        callbacks: {
            onChunk?: (chunk: StreamEventData) => void;
            onComplete?: () => void;
            onError?: (error: Error) => void;
        } = {}
    ): Promise<AbortController> {
        const abortController = new AbortController();

        const { message, userId, chatId, auth } = request;
        const { onChunk = () => {}, onComplete = () => {}, onError } = callbacks;

        try {
            const payload: ChatRequest = {
                message,
                userId,
                chatId,
                chatModel: ChatModel.GPT_4O_MINI,
            };

            const response = await makeAuthenticatedStreamRequest(
                chatbotEndpoints.chatStream,
                payload,
                auth,
                abortController
            );

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error("No response body reader available");
            }

            await processStream(reader, onChunk, onComplete);
        } catch (error) {
            if (abortController.signal.aborted) {
                console.log("Stream aborted by user");
            } else {
                console.error("Failed to stream message to chatbot:", error);
                if (onError) {
                    onError(normalizeError(error));
                }
            }
        }

        return abortController;
    }

    /**
     * Clear chat history from backend
     */
    async clearChatHistory(sessionId: string, userId?: string): Promise<{ success: boolean; message: string }> {
        try {
            const response = await callSvcAxiosInstance.delete(chatbotEndpoints.clearHistory, {
                params: {
                    sessionId,
                    ...(userId && { userId }),
                },
            });
            return response.data;
        } catch (error) {
            console.error("Failed to clear chat history:", error);
            throw normalizeError(error);
        }
    }

    /**
     * Cancel an ongoing stream
     */
    cancelStream(abortController: AbortController): void {
        abortController.abort();
    }
}

export const chatbotService = new ChatbotService();
